import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  User, 
  Settings, 
  LogOut, 
  LogIn,
  X,
  Camera,
  Edit3,
  Save,
  Users,
  UserPlus,
  BookOpen,
  Heart,
  MessageCircle,
  Globe,
  FileText,
  Palette,
  Bell,
  Shield,
  Download,
  Upload,
  HelpCircle,
  Mail,
  Calendar,
  Eye,
  EyeOff,
  HardDrive,
  Activity,
  Database,
  CheckCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  TrendingUp,
  Zap,
  Cpu,
  Wifi,
  BarChart3
} from 'lucide-react'
import { useAuth } from '../stores/authStore'
import AuthService from '../services/authService'
import settingsService from '../services/settingsService'
import imageStorageService from '../services/imageStorageService'
import simpleDataPersistenceService from '../services/simpleDataPersistenceServiceV2'
import localCloudService from '../services/localCloudService'
import { useStoryStore } from '../stores/storyStore'
import { usePlayCreateStore } from '../stores/playCreateStore'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import playCreateStorageService from '../services/playCreateStorageService'
import performanceMonitor from '../services/performanceMonitor'
import memoryManager from '../services/memoryManager'
import renderOptimizer from '../services/renderOptimizer'
import networkOptimizer from '../services/networkOptimizer'
import StorageMonitor from './StorageMonitor'
import toast from 'react-hot-toast'

export const UserCenter = ({ isOpen, onClose }) => {
  const { user, isAuthenticated, logout, updateUser } = useAuth()
  const { stories } = useStoryStore()
  const { characters } = usePlayCreateStore()
  
  // 侧边栏菜单
  const [activeMenu, setActiveMenu] = useState('profile') // 'profile' | 'account' | 'settings' | 'works'
  
  // 用户数据
  const [userData, setUserData] = useState(null)
  const [stats, setStats] = useState({
    followers: 0,
    following: 0,
    stories: 0,
    characters: 0,
    worlds: 0
  })
  
  // 编辑状态
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({})
  
  // 加载用户数据
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadUserData()
      loadStats()
    }
  }, [isOpen, isAuthenticated])

  const loadUserData = () => {
    const currentUser = AuthService.getCurrentUser()
    setUserData(currentUser)
    setEditData({
      displayName: currentUser?.profile?.displayName || '',
      bio: currentUser?.profile?.bio || '',
      email: currentUser?.email || ''
    })
  }

  const loadStats = async () => {
    try {
      // 加载故事数量
      const storiesCount = stories.length
      
      // 加载角色数量
      const charactersCount = characters.length
      
      // 加载世界数量
      const allWorlds = await worldSimulatorStorageService.getAllWorlds()
      const worldsCount = allWorlds.filter(w => 
        w.creatorId === user?.id || 
        w.creator?.userId === user?.id ||
        w.creatorId === 'default-user'
      ).length
      
      // TODO: 加载粉丝和关注数（需要实现社交功能）
      setStats({
        followers: 0, // 待实现
        following: 0, // 待实现
        stories: storiesCount,
        characters: charactersCount,
        worlds: worldsCount
      })
    } catch (error) {
      console.error('加载统计数据失败:', error)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('已安全登出')
      onClose()
    } catch (error) {
      toast.error('登出失败')
    }
  }

  if (!isOpen) return null

  // 未登录状态
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-dark-800 rounded-xl p-8 max-w-md w-full mx-4 border border-dark-600">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">用户中心</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-dark-700 rounded transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="text-center py-12">
            <User className="w-16 h-16 text-dark-600 mx-auto mb-4" />
            <p className="text-dark-300 mb-6">请先登录以访问用户中心</p>
            <button
              onClick={() => {
                onClose()
                // 触发登录弹窗
                window.dispatchEvent(new CustomEvent('openAuthModal'))
              }}
              className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
            >
              <LogIn className="w-5 h-5" />
              登录
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-xl w-full max-w-6xl h-[90vh] flex border border-dark-600 overflow-hidden relative">
        {/* 关闭按钮 - 放在窗口右上角 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 hover:bg-dark-700 rounded-lg transition-colors"
          title="关闭"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* 侧边栏 */}
        <div className="w-64 bg-dark-900 border-r border-dark-700 flex flex-col">
          <div className="p-4 border-b border-dark-700">
            <h2 className="text-lg font-bold text-white">用户中心</h2>
          </div>
          
          <nav className="flex-1 overflow-y-auto p-2">
            <button
              onClick={() => setActiveMenu('profile')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
                activeMenu === 'profile'
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              <User className="w-5 h-5" />
              个人资料
            </button>
            
            <button
              onClick={() => setActiveMenu('account')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
                activeMenu === 'account'
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              <Shield className="w-5 h-5" />
              账户信息
            </button>
            
            <button
              onClick={() => setActiveMenu('works')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
                activeMenu === 'works'
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              我的作品
            </button>
            
            <button
              onClick={() => setActiveMenu('settings')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
                activeMenu === 'settings'
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'text-dark-300 hover:text-white hover:bg-dark-700'
              }`}
            >
              <Settings className="w-5 h-5" />
              用户设置
            </button>
          </nav>
          
          <div className="p-4 border-t border-dark-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              退出登录
            </button>
          </div>
        </div>

        {/* 主内容区 */}
        <div className="flex-1 overflow-y-auto">
          {activeMenu === 'profile' && (
            <ProfileSection 
              user={userData}
              stats={stats}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              editData={editData}
              setEditData={setEditData}
              onUpdate={loadUserData}
            />
          )}
          
          {activeMenu === 'account' && (
            <AccountSection user={userData} />
          )}
          
          {activeMenu === 'works' && (
            <WorksSection stats={stats} onClose={onClose} />
          )}
          
          {activeMenu === 'settings' && (
            <SettingsSection />
          )}
        </div>
      </div>
    </div>
  )
}

// 个人资料部分
const ProfileSection = ({ user, stats, isEditing, setIsEditing, editData, setEditData, onUpdate }) => {
  const { updateUser } = useAuth()
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    try {
      setIsSaving(true)
      await updateUser({
        profile: {
          displayName: editData.displayName,
          bio: editData.bio
        },
        email: editData.email
      })
      toast.success('保存成功')
      setIsEditing(false)
      onUpdate()
    } catch (error) {
      console.error('保存失败:', error)
      toast.error('保存失败')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="p-8">
      {/* 用户头像和基本信息 */}
      <div className="flex items-start gap-6 mb-8">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-3xl font-bold">
            {user?.profile?.displayName?.charAt(0) || user?.username?.charAt(0) || 'U'}
          </div>
          {isEditing && (
            <button className="absolute bottom-0 right-0 p-2 bg-primary rounded-full hover:bg-primary-dark transition-colors">
              <Camera className="w-4 h-4 text-white" />
            </button>
          )}
        </div>
        
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-dark-300 mb-2">显示名称</label>
                <input
                  type="text"
                  value={editData.displayName}
                  onChange={(e) => setEditData({ ...editData, displayName: e.target.value })}
                  className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-2">个人简介</label>
                <textarea
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  保存
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {user?.profile?.displayName || user?.username || '用户'}
              </h1>
              <p className="text-dark-300 mb-4">
                {user?.profile?.bio || '这个人很懒，什么都没有留下...'}
              </p>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Edit3 className="w-4 h-4" />
                编辑资料
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 统计数据 */}
      <div className="grid grid-cols-5 gap-4 mb-8">
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
          <div className="text-2xl font-bold text-white mb-1">{stats.followers}</div>
          <div className="text-sm text-dark-300">粉丝</div>
        </div>
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
          <div className="text-2xl font-bold text-white mb-1">{stats.following}</div>
          <div className="text-sm text-dark-300">关注</div>
        </div>
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
          <div className="text-2xl font-bold text-white mb-1">{stats.stories}</div>
          <div className="text-sm text-dark-300">故事</div>
        </div>
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
          <div className="text-2xl font-bold text-white mb-1">{stats.characters}</div>
          <div className="text-sm text-dark-300">角色</div>
        </div>
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
          <div className="text-2xl font-bold text-white mb-1">{stats.worlds}</div>
          <div className="text-sm text-dark-300">世界</div>
        </div>
      </div>
    </div>
  )
}

// 账户信息部分
const AccountSection = ({ user }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('两次输入的密码不一致')
      return
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('密码长度至少6位')
      return
    }
    
    try {
      setIsChangingPassword(true)
      // TODO: 实现密码修改API
      toast.success('密码修改成功')
      setShowPasswordForm(false)
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      console.error('修改密码失败:', error)
      toast.error('修改密码失败')
    } finally {
      setIsChangingPassword(false)
    }
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-white mb-6">账户信息</h2>
      
      {/* 基本信息 */}
      <div className="bg-dark-900 rounded-lg p-6 border border-dark-700 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">基本信息</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-dark-300 mb-2">用户名</label>
            <div className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white">
              {user?.username || '未设置'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-dark-300 mb-2">邮箱</label>
            <div className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white">
              {user?.email || '未设置'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-dark-300 mb-2">用户ID</label>
            <div className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-dark-400 text-sm">
              {user?.id || '未设置'}
            </div>
          </div>
          <div>
            <label className="block text-sm text-dark-300 mb-2">注册时间</label>
            <div className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-dark-400">
              {user?.createdAt ? new Date(user.createdAt).toLocaleString('zh-CN') : '未知'}
            </div>
          </div>
        </div>
      </div>

      {/* 密码修改 */}
      <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">密码安全</h3>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors text-sm"
            >
              修改密码
            </button>
          )}
        </div>
        
        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm text-dark-300 mb-2">当前密码</label>
              <div className="relative">
                <input
                  type={showPasswords.old ? 'text' : 'password'}
                  value={passwordData.oldPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                  className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-dark-400 hover:text-white"
                >
                  {showPasswords.old ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-dark-300 mb-2">新密码</label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-dark-400 hover:text-white"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-dark-300 mb-2">确认新密码</label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-dark-400 hover:text-white"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isChangingPassword}
                className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isChangingPassword ? '修改中...' : '确认修改'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false)
                  setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' })
                }}
                className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
              >
                取消
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// 我的作品部分
const WorksSection = ({ stats, onClose }) => {
  const navigate = useNavigate()
  
  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-white mb-6">我的作品</h2>
      
      {/* 作品统计卡片 */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div 
          onClick={() => navigate('/my-world?tab=stories')}
          className="bg-dark-900 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-all cursor-pointer"
        >
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-6 h-6 text-primary" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.stories}</div>
              <div className="text-sm text-dark-300">故事</div>
            </div>
          </div>
          <div className="text-xs text-dark-400 mt-2">点击查看详情</div>
        </div>
        
        <div 
          onClick={() => navigate('/my-world?tab=characters')}
          className="bg-dark-900 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-all cursor-pointer"
        >
          <div className="flex items-center gap-3 mb-2">
            <MessageCircle className="w-6 h-6 text-primary" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.characters}</div>
              <div className="text-sm text-dark-300">角色</div>
            </div>
          </div>
          <div className="text-xs text-dark-400 mt-2">点击查看详情</div>
        </div>
        
        <div 
          onClick={() => navigate('/my-world?tab=worlds')}
          className="bg-dark-900 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-all cursor-pointer"
        >
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-6 h-6 text-primary" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.worlds}</div>
              <div className="text-sm text-dark-300">世界</div>
            </div>
          </div>
          <div className="text-xs text-dark-400 mt-2">点击查看详情</div>
        </div>
      </div>

      {/* 快速操作 */}
      <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
        <h3 className="text-lg font-semibold text-white mb-4">快速操作</h3>
        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={() => {
              navigate('/')
              onClose()
            }}
            className="px-4 py-3 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <FileText className="w-5 h-5" />
            创建故事
          </button>
          <button
            onClick={() => {
              navigate('/play-create')
              window.dispatchEvent(new CustomEvent('closeUserCenter'))
            }}
            className="px-4 py-3 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            创建角色
          </button>
          <button
            onClick={() => {
              navigate('/world-simulator/create')
              window.dispatchEvent(new CustomEvent('closeUserCenter'))
            }}
            className="px-4 py-3 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Globe className="w-5 h-5" />
            创建世界
          </button>
        </div>
      </div>
    </div>
  )
}

// 用户设置部分
const SettingsSection = () => {
  const [settingsTab, setSettingsTab] = useState('preferences') // 'preferences' | 'api' | 'data' | 'storage' | 'performance'
  const [settings, setSettings] = useState(() => settingsService.getSettings())
  const [preferences, setPreferences] = useState({
    language: '中文',
    theme: 'dark',
    autoSave: true,
    notifications: true,
    soundEffects: true,
    animations: true
  })
  const [isSaving, setIsSaving] = useState(false)
  
  // 存储管理相关状态
  const [storageUsage, setStorageUsage] = useState(null)
  const [storageInfo, setStorageInfo] = useState({
    used: 0,
    maxSize: 0,
    usagePercent: 0,
    itemCount: 0
  })
  const [showStorageMonitor, setShowStorageMonitor] = useState(false)
  
  // 数据管理相关状态
  const [dataStats, setDataStats] = useState(null)
  const [syncStatus, setSyncStatus] = useState(null)
  
  // 性能监控相关状态
  const [performanceMetrics, setPerformanceMetrics] = useState({})
  const [isMonitoring, setIsMonitoring] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const { stories, characters } = useStoryStore()

  React.useEffect(() => {
    const savedPreferences = localStorage.getItem('userPreferences')
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences))
    }
    
    // 加载存储使用情况
    try {
      const usage = imageStorageService.getStorageUsage()
      setStorageUsage(usage)
    } catch (error) {
      console.error('获取存储使用情况失败:', error)
    }
    
    // 检查存储空间
    checkStorageSpace()
    
    // 加载数据统计
    loadDataStats()
    
    // 启动性能监控
    startPerformanceMonitoring()
    
    return () => {
      if (isMonitoring) {
        setIsMonitoring(false)
      }
    }
  }, [])
  
  // 加载数据统计
  const loadDataStats = async () => {
    try {
      const statsResult = await simpleDataPersistenceService.getStorageStats()
      if (statsResult.success) {
        setDataStats(statsResult.stats)
      }
      setSyncStatus({
        inProgress: false,
        lastSync: statsResult.stats?.lastSync || null,
        conflictCount: 0,
        queueSize: 0,
        networkStatus: navigator.onLine ? 'online' : 'offline'
      })
    } catch (error) {
      console.error('加载数据统计失败:', error)
    }
  }
  
  // 数据同步
  const handleSync = async () => {
    try {
      setSyncStatus(prev => ({ ...prev, inProgress: true }))
      const result = await simpleDataPersistenceService.syncData()
      if (result.success) {
        toast.success('数据同步成功')
        loadDataStats()
      } else {
        toast.error('数据同步失败')
      }
    } catch (error) {
      console.error('数据同步失败:', error)
      toast.error('数据同步失败')
    } finally {
      setSyncStatus(prev => ({ ...prev, inProgress: false }))
    }
  }
  
  // 创建备份
  const handleCreateBackup = async () => {
    try {
      const allData = await simpleDataPersistenceService.getAllData('guest')
      if (allData.success) {
        const backup = {
          version: '1.0.0',
          timestamp: Date.now(),
          data: allData.data
        }
        
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `backup_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        toast.success('备份导出成功')
      } else {
        toast.error('备份创建失败')
      }
    } catch (error) {
      console.error('备份创建失败:', error)
      toast.error('备份创建失败')
    }
  }
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '从未'
    return new Date(timestamp).toLocaleString('zh-CN')
  }
  
  React.useEffect(() => {
    if (isMonitoring && autoRefresh) {
      const interval = setInterval(updatePerformanceMetrics, 5000)
      return () => clearInterval(interval)
    }
  }, [isMonitoring, autoRefresh])

  // 辅助函数
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatTime = (ms) => {
    if (!ms) return '0ms'
    if (ms < 1000) return `${ms.toFixed(2)}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400'
    if (score >= 70) return 'text-yellow-400'
    return 'text-red-400'
  }

  // 检查存储空间
  const checkStorageSpace = async () => {
    try {
      const localUsed = JSON.stringify(localStorage).length
      const localMaxSize = 3 * 1024 * 1024
      const localUsagePercent = (localUsed / localMaxSize) * 100
      const localItemCount = localStorage.length
      
      const cloudStats = await localCloudService.getStorageStats()
      const totalUsed = localUsed + cloudStats.totalSize
      const totalMaxSize = localMaxSize + (200 * 1024 * 1024)
      const totalUsagePercent = (totalUsed / totalMaxSize) * 100
      
      setStorageInfo({
        used: totalUsed,
        maxSize: totalMaxSize,
        usagePercent: totalUsagePercent,
        itemCount: localItemCount + cloudStats.fileCount
      })
    } catch (error) {
      console.error('检查存储空间失败:', error)
    }
  }

  // 性能监控
  const startPerformanceMonitoring = () => {
    setIsMonitoring(true)
    updatePerformanceMetrics()
  }

  const updatePerformanceMetrics = () => {
    const perfMetrics = performanceMonitor.getMetrics()
    const perfSummary = performanceMonitor.getSummary()
    const memStats = memoryManager.getMemoryStats()
    const renderStats = renderOptimizer.getRenderStats()
    const netStats = networkOptimizer.getNetworkStats()
    
    setPerformanceMetrics({
      performance: perfMetrics,
      summary: perfSummary,
      memory: memStats,
      render: renderStats,
      network: netStats
    })
  }

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true)
      settingsService.saveSettings(settings)
      localStorage.setItem('userPreferences', JSON.stringify(preferences))
      toast.success('设置已保存')
    } catch (error) {
      console.error('保存设置失败:', error)
      toast.error('保存设置失败')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-white mb-6">用户设置</h2>
      
      {/* 子标签页 */}
      <div className="flex gap-2 mb-6 border-b border-dark-700">
        <button
          onClick={() => setSettingsTab('preferences')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            settingsTab === 'preferences'
              ? 'text-primary border-primary'
              : 'text-dark-300 border-transparent hover:text-white'
          }`}
        >
          偏好设置
        </button>
        <button
          onClick={() => setSettingsTab('api')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            settingsTab === 'api'
              ? 'text-primary border-primary'
              : 'text-dark-300 border-transparent hover:text-white'
          }`}
        >
          API设置
        </button>
        <button
          onClick={() => setSettingsTab('data')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            settingsTab === 'data'
              ? 'text-primary border-primary'
              : 'text-dark-300 border-transparent hover:text-white'
          }`}
        >
          数据管理
        </button>
        <button
          onClick={() => setSettingsTab('storage')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            settingsTab === 'storage'
              ? 'text-primary border-primary'
              : 'text-dark-300 border-transparent hover:text-white'
          }`}
        >
          存储管理
        </button>
        <button
          onClick={() => setSettingsTab('performance')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            settingsTab === 'performance'
              ? 'text-primary border-primary'
              : 'text-dark-300 border-transparent hover:text-white'
          }`}
        >
          性能监控
        </button>
      </div>

      {/* 偏好设置 */}
      {settingsTab === 'preferences' && (
      <div className="bg-dark-900 rounded-lg p-6 border border-dark-700 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5" />
          偏好设置
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white">语言</label>
              <p className="text-sm text-dark-300">选择界面语言</p>
            </div>
            <select
              value={preferences.language}
              onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
              className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
            >
              <option value="中文">中文</option>
              <option value="English">English</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white">主题</label>
              <p className="text-sm text-dark-300">选择界面主题</p>
            </div>
            <select
              value={preferences.theme}
              onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
              className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
            >
              <option value="dark">深色</option>
              <option value="light">浅色</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white">自动保存</label>
              <p className="text-sm text-dark-300">自动保存编辑内容</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.autoSave}
              onChange={(e) => setPreferences({ ...preferences, autoSave: e.target.checked })}
              className="toggle toggle-primary"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white">通知</label>
              <p className="text-sm text-dark-300">接收系统通知</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.notifications}
              onChange={(e) => setPreferences({ ...preferences, notifications: e.target.checked })}
              className="toggle toggle-primary"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white">音效</label>
              <p className="text-sm text-dark-300">启用界面音效</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.soundEffects}
              onChange={(e) => setPreferences({ ...preferences, soundEffects: e.target.checked })}
              className="toggle toggle-primary"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white">动画</label>
              <p className="text-sm text-dark-300">启用界面动画</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.animations}
              onChange={(e) => setPreferences({ ...preferences, animations: e.target.checked })}
              className="toggle toggle-primary"
            />
          </div>
        </div>
      </div>
      )}

      {/* API设置 */}
      {settingsTab === 'api' && (
      <div className="bg-dark-900 rounded-lg p-6 border border-dark-700 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          API设置
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-dark-300 mb-2">语言模型API URL</label>
            <input
              type="text"
              value={settings.languageApiUrl || ''}
              onChange={(e) => setSettings({ ...settings, languageApiUrl: e.target.value })}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
              placeholder="https://api.example.com/v1/chat"
            />
          </div>
          <div>
            <label className="block text-sm text-dark-300 mb-2">图片生成API URL</label>
            <input
              type="text"
              value={settings.imageApiUrl || ''}
              onChange={(e) => setSettings({ ...settings, imageApiUrl: e.target.value })}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
              placeholder="https://api.example.com/v1/images"
            />
          </div>
        </div>
      </div>
      )}

      {/* 数据管理 */}
      {settingsTab === 'data' && (
      <div className="space-y-6">
        {/* 存储概览 */}
        {dataStats && (
          <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" />
              存储概览
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <HardDrive className="text-blue-400" size={16} />
                  <span className="text-sm text-dark-300">总大小</span>
                </div>
                <div className="text-xl font-bold text-white">
                  {formatBytes(dataStats.totalSize || 0)}
                </div>
              </div>
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="text-green-400" size={16} />
                  <span className="text-sm text-dark-300">项目数量</span>
                </div>
                <div className="text-xl font-bold text-white">
                  {dataStats.itemCount || 0}
                </div>
              </div>
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-yellow-400" size={16} />
                  <span className="text-sm text-dark-300">最后同步</span>
                </div>
                <div className="text-sm text-white">
                  {formatDate(dataStats.lastSync)}
                </div>
              </div>
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="text-green-400" size={16} />
                  <span className="text-sm text-dark-300">压缩状态</span>
                </div>
                <div className="text-sm text-white">
                  {dataStats.compressionEnabled ? '已启用' : '已禁用'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 数据同步 */}
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              数据同步
            </h3>
            <button
              onClick={handleSync}
              disabled={syncStatus?.inProgress}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncStatus?.inProgress ? 'animate-spin' : ''}`} />
              {syncStatus?.inProgress ? '同步中...' : '立即同步'}
            </button>
          </div>
          {syncStatus && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  syncStatus.inProgress ? 'bg-yellow-400' : 
                  syncStatus.networkStatus === 'online' ? 'bg-green-400' : 'bg-red-400'
                }`} />
                <span className="text-white">
                  {syncStatus.inProgress ? '同步中...' : 
                   syncStatus.networkStatus === 'online' ? '在线' : '离线'}
                </span>
              </div>
              <div className="text-sm text-dark-300">
                最后同步: {formatDate(syncStatus.lastSync)}
              </div>
              <div className="text-sm text-dark-300">
                冲突数量: {syncStatus.conflictCount}
              </div>
            </div>
          )}
        </div>

        {/* 数据备份 */}
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Download className="w-5 h-5" />
              数据备份
            </h3>
            <button
              onClick={handleCreateBackup}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              <Download size={16} />
              创建备份
            </button>
          </div>
          <p className="text-sm text-dark-300">
            备份所有数据到本地文件，用于数据恢复和迁移。
          </p>
        </div>
      </div>
      )}

      {/* 存储管理 */}
      {settingsTab === 'storage' && (
      <div className="space-y-6">
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <HardDrive className="w-5 h-5" />
              存储空间
            </h3>
            <button
              onClick={() => setShowStorageMonitor(true)}
              className="px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors text-sm"
            >
              详细管理
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-dark-300">已使用</span>
              <span className="text-white font-mono">{formatBytes(storageInfo.used)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-300">总空间</span>
              <span className="text-white font-mono">{formatBytes(storageInfo.maxSize)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-300">使用率</span>
              <span className={`font-mono ${
                storageInfo.usagePercent >= 90 ? 'text-red-400' :
                storageInfo.usagePercent >= 70 ? 'text-yellow-400' : 'text-green-400'
              }`}>
                {storageInfo.usagePercent.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-dark-800 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all ${
                  storageInfo.usagePercent >= 90 ? 'bg-red-500' :
                  storageInfo.usagePercent >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(storageInfo.usagePercent, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 性能监控 */}
      {settingsTab === 'performance' && (
      <div className="space-y-6">
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5" />
              性能监控
            </h3>
            <label className="flex items-center gap-2 text-sm text-dark-300">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="toggle toggle-primary"
              />
              自动刷新
            </label>
          </div>
          
          {performanceMetrics.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="text-sm text-dark-300 mb-1">整体性能</div>
                <div className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary.overallScore || 0)}`}>
                  {performanceMetrics.summary.overallScore || 0}
                </div>
              </div>
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="text-sm text-dark-300 mb-1">页面加载</div>
                <div className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary.pageLoadScore || 0)}`}>
                  {performanceMetrics.summary.pageLoadScore || 0}
                </div>
              </div>
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="text-sm text-dark-300 mb-1">资源加载</div>
                <div className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary.resourceScore || 0)}`}>
                  {performanceMetrics.summary.resourceScore || 0}
                </div>
              </div>
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="text-sm text-dark-300 mb-1">运行时</div>
                <div className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary.runtimeScore || 0)}`}>
                  {performanceMetrics.summary.runtimeScore || 0}
                </div>
              </div>
            </div>
          )}
          
          {performanceMetrics.memory && (
            <div className="bg-dark-800 rounded-lg p-4">
              <div className="text-sm text-dark-300 mb-2">内存使用</div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-dark-400">已使用</div>
                  <div className="text-white">{formatBytes(performanceMetrics.memory.used || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-dark-400">总内存</div>
                  <div className="text-white">{formatBytes(performanceMetrics.memory.total || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-dark-400">使用率</div>
                  <div className="text-white">
                    {performanceMetrics.memory.total > 0
                      ? `${((performanceMetrics.memory.used / performanceMetrics.memory.total) * 100).toFixed(1)}%`
                      : '0%'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* 保存按钮 */}
      {(settingsTab === 'preferences' || settingsTab === 'api') && (
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-5 h-5" />
          {isSaving ? '保存中...' : '保存设置'}
        </button>
      </div>
      )}

      {/* 存储监控器 */}
      {showStorageMonitor && (
        <StorageMonitor onClose={() => setShowStorageMonitor(false)} />
      )}
    </div>
  )
}

